import { AccessRequestElementInfo, homePage } from 'api/home-page';
import { useQuery } from '@tanstack/react-query';
import { Box, Button, Stack } from '@mui/material';
import useBundleTranslation from 'i18n';
import { useConfigConstantValue } from 'hooks/useConfigConstant';
import IconMi from 'components/common/icon/IconMi';
import React, { useMemo, useState } from 'react';
import { TileInfo } from 'app/home/index.functions';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { alpha } from '@mui/material/styles';
import { IElementData, IElementDataSettings } from 'components/common/element-data/types';
import { useElementMetadataSettings } from 'hooks/useElementMetadata';
import { format } from 'date-fns/format';
import { adjustTopicsDataFormat } from 'components/common/element-data/functions';
import { Popup } from 'components/common/popup/Popup';
import { useSelector } from 'react-redux';
import ElementDataInfo from 'components/common/element-data/ElementDataInfo';
import { UserAuth } from 'store/auth';
import { useAppSelector } from 'store/hooks';
import isOwnerVisible from 'tools/owners';

interface IProps {
    elementId: number;
    segmentValueId: number;
    errorMessage?: string;
    onClose?: () => void;
    isPopup?: boolean;
}

export default function DiscoverableDenyPage({ elementId, segmentValueId, errorMessage, onClose, isPopup }: IProps) {
    const { t } = useBundleTranslation(['app/home', 'components/error/page_error_block']);

    const queryKey = `element_preview_deny_${elementId}_${segmentValueId}`;
    const okButtonText = useConfigConstantValue('ACCESS_REQUEST_BUTTON_TEXT');
    const supportAdmins = useSelector((state: any) => state.auth?.userInfo?.supportAdmins) ?? [];
    const accessRequestUrl = useConfigConstantValue('ACCESS_REQUEST_URL');
    const accessRequestViaWebpage = useConfigConstantValue('ACCESS_REQUEST_VIA_WEBPAGE');
    const showDocuments = useConfigConstantValue('DISCOVERABLE_CONTENT_DISPLAYS_DOCUMENTS');

    const [showMessagePopup, setShowMessagePopup] = useState(false);
    const [requestMessage, setRequestMessage] = useState('');

    const { configuration }: UserAuth = useAppSelector((state) => state.auth);
    let metadataSettings = useElementMetadataSettings('viewer');

    const { data: accessRequestInfoData } = useQuery<AccessRequestElementInfo, Error>({
        queryKey: [queryKey],
        queryFn: () => {
            return homePage.getAccessRequestInfo(elementId, segmentValueId);
        },
    });

    const onRequestAccess = async () => {
        if (accessRequestViaWebpage && accessRequestUrl) {
            window.open(accessRequestUrl, '_blank');
            if (onClose) {
                onClose();
            }
            return;
        }
        let data = await homePage.requestAccess(elementId, segmentValueId);
        setRequestMessage(data?.message ? data.message : t('access_request_sent'));
        setShowMessagePopup(true);
    };

    const onMessagePopupHide = () => {
        setShowMessagePopup(false);
        if (onClose) {
            onClose();
        }
    };

    const elementData: IElementData = useMemo(() => {
        const tileInfo = accessRequestInfoData?.accessData as TileInfo;
        return {
            elementId: elementId,
            elementType: tileInfo?.element_type,
            description: accessRequestInfoData?.accessData?.description,
            certifiedInd: tileInfo?.certified_ind === 'Y',
            certificationLevelColor: tileInfo?.certification_level_color ?? 'success.main',
            certificationLevelName: tileInfo?.certification_level_name,
            lastCertifiedByName: tileInfo?.last_certified_by_name,
            lastCertifiedTime:
                tileInfo?.last_certified_time && '0000-00-00 00:00:00' != tileInfo.last_certified_time
                    ? format(new Date(tileInfo.last_certified_time), 'M/dd/yyyy')
                    : '',
            contentType: accessRequestInfoData?.accessData?.content_type,
            topics: accessRequestInfoData?.accessData?.topics ? accessRequestInfoData.accessData.topics : undefined,
            businessOwner: tileInfo?.business_owner,
            businessOwnerEmail: tileInfo?.business_owner_email,
            technicalOwner: tileInfo?.technical_owner,
            technicalOwnerEmail: tileInfo?.technical_owner_email,
            dataSteward: tileInfo?.data_steward,
            dataStewardEmail: tileInfo?.data_steward_email,
            projected: tileInfo?.total_forecast_amount_formatted,
            dataSourceName: tileInfo?.data_source_name,
            lastDisplayGenerationTime: tileInfo?.last_display_generation_time,
            lastFileUpdatedTime: tileInfo?.last_file_updated_time,
            engagement: tileInfo?.global_total_view_count,
            customFields: accessRequestInfoData?.accessData?.custom_fields,
            documents: accessRequestInfoData?.accessData?.documents,
        };
    }, [accessRequestInfoData?.accessData]);

    if (metadataSettings.fields['documents']?.display) {
        metadataSettings.fields['documents'].display = showDocuments;
    }

    ['business_owner', 'technical_owner', 'data_steward'].forEach((ownerField) => {
        if (metadataSettings.fields[ownerField]?.display) {
            metadataSettings.fields[ownerField].display =
                isOwnerVisible((configuration.owners as any)[ownerField], 'elements') &&
                isOwnerVisible((configuration.owners as any)[ownerField], 'tile_preview');
        }
    });

    const elementDataSettings: IElementDataSettings = {
        viewSize: 'medium',
        metadata: metadataSettings,
    };

    if (!accessRequestInfoData) {
        return <LoadingPlaceholder />;
    }

    return (
        <>
            <Stack direction={'row'} spacing={3}>
                <Box sx={{ width: '100%' }}>
                    <Stack direction={'row'} spacing={3}>
                        <Box flexGrow={1}>
                            {!isPopup && (
                                <Box sx={{ fontSize: '20px', lineHeight: '26px', fontWeight: 600, pb: 1 }}>
                                    {t('access_denied')}
                                </Box>
                            )}
                            <Box sx={{ pb: 2, fontSize: '14px' }}>
                                {errorMessage && <Box dangerouslySetInnerHTML={{ __html: errorMessage }} />}
                                <Box
                                    dangerouslySetInnerHTML={{ __html: String(accessRequestInfoData.html) }}
                                    className={'markdown-holder'}
                                />
                            </Box>
                            <Box>
                                <Button onClick={onRequestAccess}>{okButtonText}</Button>
                            </Box>
                        </Box>
                        {accessRequestInfoData.accessData?.image && (
                            <Box flexShrink={0}>
                                <Box
                                    component={'img'}
                                    src={accessRequestInfoData.accessData?.image}
                                    style={{ maxHeight: '100px', maxWidth: '180px' }}
                                />
                            </Box>
                        )}
                    </Stack>
                    {supportAdmins.length > 0 && (
                        <Box
                            sx={{
                                mt: 4,
                                pt: 4,
                                borderTop: (theme) => `1px solid ${alpha(theme.palette.text.primary, 0.24)}`,
                            }}
                        >
                            <Box sx={{ pb: 1.5 }}>{t('access_support_administrators')}</Box>
                            <Stack spacing={0.5}>
                                {supportAdmins.map((user: any) => {
                                    return (
                                        <Stack sx={{ color: 'primary.main' }} direction="row" key={user.username}>
                                            <Box
                                                sx={{
                                                    display: 'inline-flex',
                                                    mr: 1,
                                                    mt: '1px',
                                                    fontSize: '16px',
                                                    width: '16px',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <IconMi icon={'user-profile'} />
                                            </Box>
                                            <Box>{user.display_name}</Box>
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        </Box>
                    )}
                </Box>
                <Stack sx={{ width: '100%' }}>
                    {accessRequestInfoData.name && (
                        <Box sx={{ fontSize: '14px', fontWeight: 600, pb: '10px' }}>{accessRequestInfoData.name}</Box>
                    )}
                    <ElementDataInfo
                        elementData={elementData}
                        elementDataSettings={elementDataSettings}
                        size={elementDataSettings.viewSize}
                    />
                </Stack>
            </Stack>
            {showMessagePopup && (
                <Popup
                    settings={{
                        textOK: t('ok_button'),
                        noCancel: true,
                        needTranslation: false,
                        dataTestOkButton: 'access_denied_popup_ok_button',
                    }}
                    maxWidth="popupMd"
                    onHide={onMessagePopupHide}
                    open
                    onConfirm={onMessagePopupHide}
                >
                    {requestMessage}
                </Popup>
            )}
        </>
    );
}
